import React, { useCallback, useState } from 'react';
import { useDocumentState } from '../../../../../../contexts/DocumentContext';
import { useApolloClient, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { DocumentStatusEnum } from '../../../../../../shared/constants/document';
import { permissionLevelToBooleans } from '../../../../../../types/counterparty-permissions';
import { useDeal } from '../../../../../../contexts/DealContext';
import { shareDocumentWithReceiver } from '../../../../../../utils/api/grant-document-access';
import {
  givePossessionSuccessMessage,
  ShareWithUnresolvedSuggestionsWarning,
  successSendDocumentMessage,
} from '../../../../document-editor/constants/EditorConstats';
import { usePendingCounterpartySuggestions } from '../usePendingCounterpartySuggestions';
import { useDocumentRole } from '../../../../../../shared/hooks/useDocumentRole/useDocumentRole';
import { useNotifications } from '../../../../../../notifications';
import { paths } from '../../../../../../routing';
import { useModals } from '../../../../../../contexts/ModalsContext';
import GrantAccessButton from './grantAccessButton';
import GrantAccessSlideout from './GrantAccessSlideout';
import { mutations } from '../../../../../../api';
import { CounterpartyPermissionLevel } from '../../../../../../types/counterparty-permissions';

interface Props {
  teamId: string;
  documentId: string;
  dealId: string;
}

const GrantAccess = ({ documentId, dealId, teamId }: Props) => {
  const { state, updateDocumentState } = useDocumentState();
  const [, { error: notifyError, success: notifySuccess }] = useNotifications();
  const { showGrantAccessModal: isGrantAccessModalOpen } = useModals();
  const [showModal, setShowModal] = useState(isGrantAccessModalOpen);
  const documentStatus = state.status;
  const documentSource = state.source;
  const optionalMessageInputRef = React.useRef<HTMLTextAreaElement>(null);
  const { formatMessage } = useIntl();
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { currentDeal } = useDeal();
  const isDealShared = !!currentDeal?.receiverEmail;
  const receiverTeamId = state.receiverTeamId;
  const hasCounterparty = !!(receiverTeamId || currentDeal?.receiverEmail);
  const { userRole, isDocumentCreator } = useDocumentRole(documentId);
  const pendingCounterpartySuggestions = usePendingCounterpartySuggestions(documentId, userRole);
  const dealUrl = paths.deals.detail.replace(':teamId', teamId as string).replace(':dealId', dealId);

  // Read permission level directly from context (single source of truth)
  const counterpartyPermissionLevel = state.counterpartyPermissionLevel;

  // Mutation for immediate permission level updates (sync across all UI)
  const [setPermissionLevel] = useMutation(mutations.setCounterpartyPermissionLevel, {
    onCompleted: (data) => {
      const newLevel = data.setCounterpartyPermissionLevel.counterpartyPermissionLevel as CounterpartyPermissionLevel;
      updateDocumentState({ counterpartyPermissionLevel: newLevel, ...permissionLevelToBooleans(newLevel) });
      // Emit custom event for WebViewer DOM dropdown sync
      window.dispatchEvent(new CustomEvent('propdocs:permissionChanged', {
        detail: { type: 'level', value: newLevel }
      }));
    },
  });

  // Handler for permission level changes
  const handleCounterpartyPermissionChange = useCallback((level: CounterpartyPermissionLevel) => {
    if (documentId) {
      setPermissionLevel({ variables: { documentId, permissionLevel: level } });
    }
  }, [documentId, setPermissionLevel]);

  const handleSendDocumentMutation = useCallback(async () => {
    const additionalMessage = optionalMessageInputRef.current?.value;
    // Use Redux values (single source of truth, already persisted via immediate mutations)
    await shareDocumentWithReceiver(
      documentId,
      additionalMessage,
      counterpartyPermissionLevel
    )(apolloClient)
      .then(async () => {
        // Refetch data first, then navigate
        await apolloClient.resetStore();

        navigate(dealUrl);

        // Show success notification with delay after navigation
        setTimeout(() => {
          const successGrantAccessMessageWithEmail = {
            id: successSendDocumentMessage.id,
            defaultMessage: successSendDocumentMessage.defaultMessage,
          };
          isDealShared &&
            notifySuccess({
              message: formatMessage(
                documentStatus === DocumentStatusEnum.UPLOADED
                  ? successGrantAccessMessageWithEmail
                  : givePossessionSuccessMessage
              ),
            });
        }, 100);
      })
      .catch((error) => {
        notifyError({ message: error.message });
      });
  }, [
    apolloClient,
    dealUrl,
    documentId,
    documentStatus,
    formatMessage,
    notifyError,
    notifySuccess,
    isDealShared,
    navigate,
    counterpartyPermissionLevel,
  ]);

  const handleSendDocument = useCallback(async () => {
    setIsLoading(true);
    return await handleSendDocumentMutation();
  }, [handleSendDocumentMutation]);

  const getPendingSuggestionsAlert = () => {
    // Counted from the relational suggestion rows, NOT CKEditor's TrackChanges
    // plugin — that read returned undefined under the DOCX-native editor, so
    // this warning silently stopped appearing. See usePendingCounterpartySuggestions.
    const suggestionsNumber = pendingCounterpartySuggestions.fromCounterparty;

    if (!suggestionsNumber) return null;

    return {
      text: ShareWithUnresolvedSuggestionsWarning.text.replace('{num}', `${suggestionsNumber}`),
      title: ShareWithUnresolvedSuggestionsWarning.title,
    };
  };

  return (
    <>
      <GrantAccessButton onClick={() => setShowModal(true)} />
      <GrantAccessSlideout
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        isDealShared={isDealShared}
        hasCounterparty={hasCounterparty}
        teamId={teamId}
        handler={handleSendDocument}
        isLoading={isLoading}
        suggestionsAlert={getPendingSuggestionsAlert?.()}
        optionalMessageInputRef={optionalMessageInputRef}
        counterpartyPermissionLevel={counterpartyPermissionLevel}
        onCounterpartyPermissionChange={handleCounterpartyPermissionChange}
        documentSource={documentSource}
        documentStatus={documentStatus}
        isDocumentCreator={isDocumentCreator}
      />
    </>
  );
};

export default GrantAccess;
